import { debounce, get as _get } from "lodash-es";
import { computed, ref, inject, shallowRef, watch } from "vue";

const stations = shallowRef(null);
const loading = ref(false);

function loadStations(){
	if(loading.value) return; // another call running
	if(stations.value) return;
	loading.value = true;
	const savedStations = sessionStorage.getItem('stations');
	// disable sessionStorage online, add a expiration management
	if(savedStations && import.meta.env.DEV){
		stations.value = JSON.parse(savedStations);
		loading.value = false;
	}else{
		const axios  = inject('axios');
		axios.get('trenitalia/stations')
		.then(res => {
			stations.value = res.data;
			sessionStorage.setItem('stations', JSON.stringify(stations.value));
		}).catch(() => {})
		.finally(() => {
			loading.value = false;
		});
	}
}

export function useSearchStation(options){
	const searching = ref(false);
	const searchText = ref(null);
	const filteredStations = shallowRef([]);
	let abortController = null;
	const mode = _get(options, 'mode', 'preload');
	const selectedStation = _get(options, 'selectedStation'); // pass a computed object

	let execSearch;
	if(mode == 'preload'){
		loadStations();

		// init filteredStations
		if(stations.value){
			filteredStations.value = stations.value.slice(0, 20);
		}else{
			const stopWatchStations = watch(stations, (newStations) => {
				if(filteredStations.value.length || !newStations) return;
				filteredStations.value = newStations.slice(0, 20);
				stopWatchStations();
			});
		}

		execSearch = debounce(() => {
			if(!searchText.value || (selectedStation && selectedStation.value && searchText.value == selectedStation.value.name)) return;
			if(!searchText.value.trim()) return;
			searching.value = true;
			// use allStations if not empty
			if(stations.value && Array.isArray(stations.value) && stations.value.length > 0){
				const searchLower = searchText.value.toLowerCase();
				const intlCollator = Intl.Collator(undefined, { sensitivity: 'base', usage: 'sort', ignorePunctuation: false});
				const sortIndex = {};
				filteredStations.value = stations.value.filter(function(item){
					if(!item.name) return false;
					const nameIndex = item.name.toLowerCase().indexOf(searchLower);
					if(nameIndex != -1){
						sortIndex[item.service_id] = nameIndex;
						return true;
					}
					if(item.aliases && item.aliases.length){
						for (const alias of item.aliases) {
							if(alias.indexOf(searchLower) != -1){
								sortIndex[item.service_id] = -1;
								return true;
							}
						}
					}
					return false;
				}).sort((a, b) => {
					const i = sortIndex[a.service_id];
					const j = sortIndex[b.service_id];
					if(i == j) return intlCollator.compare(a.name, b.name);
					return (i < j && i != -1) || j == -1 ? -1 : 1
				}).slice(0, 50);
			}else filteredStations.value = [];
			searching.value = false;
		}, 300);
	}else if(mode == 'ondemand'){
		execSearch = debounce(() => {
			if(abortController) abortController.abort();
			if(!searchText.value || (selectedStation && selectedStation.value && searchText.value == selectedStation.value.name)) return;
			if (!searchText.value || searchText.value.length < 2) return;
			searching.value = true;
			abortController = new AbortController();
			return axios.get('trenitalia/elenco-stazioni', {
				signal: abortController.signal,
				params: {search: searchText.value}
			}).then(res => {
				filteredStations.value = res.data.items || res.data;
			}).catch(() => {})
			.finally(() => {
				abortController = null;
				searching.value = false;
			});
		}, 300);
	}

	watch(searchText, async (newValue) => {
		execSearch();
	});

	return {
		preLoading: loading,
		searching,
		searchText,
		filteredStations: filteredStations,
		loading: computed(() => loading.value || searching.value),
		selectedStation
	};
}

export function useStations(){
	return {
		stations,
		loading
	};
}

export function findStationByID(stationID) {
	loadStations();
	return stations.value.filter(function(item){
		return item.service_id && item.service_id == stationID;
	})[0];
}